import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ShiftEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {useShiftAction} from "./useShiftAction";
import {IconEdit, IconTrash} from "@tabler/icons-react";

const ALL_SHIFT_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type ShiftAction = typeof ALL_SHIFT_ACTIONS[number];

export type ShiftActionMenuDropdownProps = {
  shift: ShiftEntity
  actions?: ShiftAction[]
  onActionClick?: (action: ShiftAction) => void
  showEmpty?: boolean
}

export const ShiftActionMenuDropdown = (props: ShiftActionMenuDropdownProps) => {
  const {
    shift,
    actions = ALL_SHIFT_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<ShiftAction> = useMemo(() => {
    const enabledActions = new Set<ShiftAction>()
    const actionsSet = actions ? new Set<ShiftAction>(actions) : new Set<ShiftAction>()

    if (hasAccess("shift_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("shift_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [shift, actions, hasAccess])

  const {
    onShiftActionClick
  } = useShiftAction()

  const _onActionClick = useCallback((action: ShiftAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onShiftActionClick(shift, action)
    }
  }, [onActionClick, onShiftActionClick, shift])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
